import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Crown, Heart, BookOpen, Star, Sun, Users, HandHeart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import Layout from '../components/layout/Layout';
import { valuesData } from '../data/mock';

const Values = () => {
  const getIcon = (iconName) => {
    const iconProps = { className: "w-6 h-6" };
    switch (iconName) {
      case 'Crown':
        return <Crown {...iconProps} />;
      case 'Heart':
        return <Heart {...iconProps} />;
      case 'BookOpen':
        return <BookOpen {...iconProps} />;
      case 'Star':
        return <Star {...iconProps} />;
      case 'Sun':
        return <Sun {...iconProps} />;
      case 'Users':
        return <Users {...iconProps} />;
      case 'HandHeart':
        return <HandHeart {...iconProps} />;
      default:
        return <Heart {...iconProps} />;
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-[#f9f9f6] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#A88B4A] font-medium text-sm tracking-widest uppercase mb-4 block">
            What We Stand For
          </span>
          <h1 className="text-4xl md:text-5xl font-serif text-gray-800 mb-6">
            Our Core Values
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            These foundational beliefs guide every aspect of our care and define who we are in service to those we're called to help.
          </p>
        </div>
      </section>

      {/* Values List */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {valuesData.map((value, index) => (
              <div key={index} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                {/* Number */}
                <div className="lg:col-span-2">
                  <span className="text-5xl font-serif text-[#d4dcc4]">{value.number}</span>
                </div>
                
                {/* Content */}
                <div className="lg:col-span-10">
                  <h3 className="text-2xl font-serif text-gray-800 mb-4">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {value.description}
                  </p>
                  
                  {/* Quote Card */}
                  <Card className="bg-[#f5f5f0] border-0 p-6 rounded-xl">
                    <p className="text-gray-700 italic font-serif mb-2">
                      "{value.quote.text}"
                    </p>
                    <p className="text-[#A88B4A] text-sm font-medium mb-4">
                      {value.quote.reference}
                    </p>
                    <p className="text-gray-600 text-sm">
                      {value.affirmation}
                    </p>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom Quote Section */}
      <section className="py-16 bg-[#f9f9f6]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="bg-gradient-to-br from-[#d4dcc4] to-[#c9b896] rounded-2xl p-12">
            <Heart className="w-12 h-12 mx-auto mb-6 text-[#8B9D77] stroke-1" />
            <p className="text-xl md:text-2xl italic text-gray-800 font-serif mb-4">
              "I have called you by name; you are mine."
            </p>
            <p className="text-[#A88B4A] font-medium">Isaiah 43:1</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-gray-800 mb-6">
            Experience Care Rooted in These Values
          </h2>
          <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
            Learn how our values translate into meaningful, compassionate services for you and your loved ones.
          </p>
          <Link to="/services">
            <Button className="bg-[#A88B4A] hover:bg-[#967A3E] text-white px-10 py-6 rounded-full text-base font-medium transition-all duration-300 hover:shadow-lg flex items-center gap-2 mx-auto">
              Explore Our Services
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default Values;
